/*
 * Copyright 2025 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.settings

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import studio.oddpods.cauce.core.base.analytics.AnalyticsHelper
import studio.oddpods.cauce.core.base.analytics.TrackScreenView
import studio.oddpods.cauce.core.base.analytics.rememberAnalyticsHelper
import studio.oddpods.cauce.core.model.display.DisplayPreferences
import studio.oddpods.cauce.core.ui.scaffold.KptScaffold
import studio.oddpods.cauce.feature.settings.generated.resources.Res
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_title
import studio.oddpods.cauce.feature.settings.sections.AccountSection
import studio.oddpods.cauce.feature.settings.sections.AppearanceSection
import studio.oddpods.cauce.feature.settings.sections.DisplayPreferencesSection
import studio.oddpods.cauce.feature.settings.sections.NotificationsSection
import studio.oddpods.cauce.feature.settings.sections.ProfileSummarySection
import studio.oddpods.cauce.feature.settings.sections.RemindersSection

@Composable
internal fun SettingsScreen(
    modifier: Modifier = Modifier,
    showNavigationIcon: Boolean = true,
    onBackClick: () -> Unit = {},
    onNavigateToReminders: () -> Unit = {},
    onNavigateToNotifications: () -> Unit = {},
    viewModel: SettingsViewmodel = koinViewModel(),
) {
    val analyticsHelper = rememberAnalyticsHelper()
    var showSettingsDialog by rememberSaveable { mutableStateOf(false) }
    if (showSettingsDialog) {
        SettingsDialog(onDismiss = {
            analyticsHelper.logSettingsDialogVisible(false)
            showSettingsDialog = false
        })
    }

    val uiState by viewModel.settingsUiState.collectAsStateWithLifecycle()

    SettingsScreenContent(
        modifier = modifier.fillMaxSize(),
        showNavigationIcon = showNavigationIcon,
        onBackClick = onBackClick,
        onNavigateToReminders = onNavigateToReminders,
        onNavigateToNotifications = onNavigateToNotifications,
        uiState = uiState,
        onThemeCardClick = {
            analyticsHelper.logSettingsDialogVisible(true)
            showSettingsDialog = true
        },
        onDisplayPrefsChanged = viewModel::onDisplayPrefsChanged,
        onLogoutClick = viewModel::onLogoutConfirmed,
    )

    TrackScreenView(screenName = "SettingsScreen")
}

@Composable
internal fun SettingsScreenContent(
    onThemeCardClick: () -> Unit,
    uiState: SettingsUiState,
    onDisplayPrefsChanged: (DisplayPreferences) -> Unit,
    onLogoutClick: () -> Unit,
    modifier: Modifier = Modifier,
    showNavigationIcon: Boolean = true,
    onBackClick: () -> Unit = {},
    onNavigateToReminders: () -> Unit = {},
    onNavigateToNotifications: () -> Unit = {},
) {
    KptScaffold(
        title = stringResource(Res.string.feature_settings_title),
        showNavigationIcon = showNavigationIcon,
        onNavigationIconClick = onBackClick,
        modifier = modifier,
    ) {
        when (uiState) {
            SettingsUiState.Loading -> { /* empty — data arrives quickly */ }
            is SettingsUiState.Success -> {
                LazyColumn(
                    modifier = Modifier.fillMaxSize(),
                    contentPadding = PaddingValues(horizontal = 16.dp, vertical = 16.dp),
                    verticalArrangement = Arrangement.spacedBy(16.dp),
                ) {
                    item { ProfileSummarySection(state = uiState.data.profile) }
                    item {
                        DisplayPreferencesSection(
                            state = uiState.data.display,
                            onChange = onDisplayPrefsChanged,
                        )
                    }
                    item { AppearanceSection(onThemeCardClick = onThemeCardClick) }
                    item { RemindersSection(onNavigateToReminders = onNavigateToReminders) }
                    item { NotificationsSection(onNavigateToNotifications = onNavigateToNotifications) }
                    item { AccountSection(onLogoutClick = onLogoutClick) }
                }
            }
        }
    }
}

private fun AnalyticsHelper.logSettingsDialogVisible(visible: Boolean) {
    logEvent(
        type = "settings_dialog_visible",
        params = mapOf("visible" to visible.toString()),
    )
}
